import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface DeleteAppDialogProps {
  appName: string;
  /** Nur managed Apps besitzen Volumes — nur dann ist die Keep-Volumes-Option sinnvoll. */
  isManaged: boolean;
  onCancel: () => void;
  onConfirm: (keepVolumes: boolean) => Promise<void>;
}

/**
 * Bestätigungsdialog fürs App-Löschen als echtes Modal-Overlay (#116).
 *
 * Frühere Variante rendert inline am Seitenende: bei langer App-Liste öffnet
 * sich der Dialog unsichtbar weit unten — „gefühlt passiert nichts". Als
 * fixed-positioniertes Overlay ist der Dialog unabhängig von Scrollposition und
 * Auslöser-Ort immer im Viewport. Wird von AdminApps (Liste) und AdminAppDetail
 * (Detailseite / Owner-Self-Service) geteilt.
 */
export function DeleteAppDialog(props: DeleteAppDialogProps): JSX.Element {
  const { t } = useTranslation();
  // Default zur destruktiv-historischen Variante (Volumes entfernen); die
  // Checkbox ist ein bewusstes Opt-in zum Behalten der Daten.
  const [keepVolumes, setKeepVolumes] = useState(false);
  const [busy, setBusy] = useState(false);
  const titleId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Escape schließt den Dialog (sofern keine Löschung läuft).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !busy) props.onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, props.onCancel]);

  // Fokus auf die sichere Default-Aktion (Abbrechen) beim Öffnen.
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  const confirm = async (): Promise<void> => {
    setBusy(true);
    try {
      await props.onConfirm(props.isManaged && keepVolumes);
    } finally {
      setBusy(false);
    }
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Backdrop-Klick ist optional; Escape (oben) deckt die Tastatur ab.
    <div
      className="modal__overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) props.onCancel();
      }}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: natives <dialog> bräuchte showModal(), das in jsdom (Komponententests) nicht implementiert ist. */}
      <div className="modal admin__form" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <h3 id={titleId}>{t("admin.delete.title", { name: props.appName })}</h3>
        <p>{t("admin.delete.body")}</p>
        {props.isManaged ? (
          <>
            <label className="admin__field admin__field--inline">
              <input
                type="checkbox"
                checked={keepVolumes}
                onChange={(e) => setKeepVolumes(e.target.checked)}
              />
              {t("admin.delete.keepVolumes")}
            </label>
            <p className="launchpad__hint">
              {keepVolumes
                ? t("admin.delete.keepVolumesHint")
                : t("admin.delete.removeVolumesHint")}
            </p>
          </>
        ) : null}
        <div className="admin__form-actions">
          <button type="button" ref={cancelRef} onClick={props.onCancel} disabled={busy}>
            {t("admin.cancel")}
          </button>
          <button
            type="button"
            className="admin__danger"
            onClick={() => void confirm()}
            disabled={busy}
          >
            {t("admin.delete.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
